import { Search, Lightbulb, Rocket, BarChart3 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const Process = () => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2, triggerOnce: true });

  const steps = [
    {
      icon: Search,
      number: "01",
      title: "Discovery",
      description: "We dive deep into your business, audience, and goals to understand exactly what success looks like for you."
    },
    {
      icon: Lightbulb,
      number: "02",
      title: "Strategy",
      description: "We craft a custom digital marketing plan tailored to your budget, timeline, and growth objectives."
    },
    {
      icon: Rocket,
      number: "03",
      title: "Execute",
      description: "Our team implements your strategy with precision—from website to ads to content, we handle it all."
    },
    {
      icon: BarChart3,
      number: "04",
      title: "Optimize",
      description: "We continuously monitor, test, and refine to maximize your results and ROI over time."
    }
  ];

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-accent/3 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className={`text-center mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How We Work Together
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A simple, proven process that gets results—without the headaches.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index} 
                  className={`relative text-center md:text-left transition-all duration-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${200 + index * 150}ms` }}
                >
                  {/* Animated connector line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-full h-[2px] overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r from-accent to-accent/20 transition-all duration-1000 ${isInView ? 'w-full' : 'w-0'}`}
                        style={{ transitionDelay: `${600 + index * 200}ms` }}
                      />
                    </div>
                  )}
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-center md:justify-start gap-4 mb-5">
                      {/* Icon with glow effect */}
                      <div className="relative group/icon cursor-pointer">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-foreground transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:shadow-lg group-hover/icon:shadow-accent/40 group-hover/icon:rotate-3">
                          <IconComponent className="h-7 w-7 transition-transform duration-300 group-hover/icon:scale-110" />
                        </div>
                        {/* Subtle glow behind icon */}
                        <div className="absolute inset-0 rounded-full bg-accent/30 blur-xl opacity-0 group-hover/icon:opacity-100 transition-opacity duration-300 -z-10" />
                      </div>
                      <span className="text-5xl font-bold text-muted-foreground/10 group-hover:text-accent/20 transition-colors duration-300">
                        {step.number}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
